import { useRequiredContext } from "@redotech/react-util/context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { ReloadTeamContext } from "@redotech/redo-merchant-app-common/team";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { memo, useCallback, useContext, useState } from "react";
import { TaskContextualModalRenderer } from "./components/contextual-modal/task-contextual-modal-renderer";
import { IntakeModal } from "./components/intake-modal/intake-modal";
import {
  SetupWelcomeGradient,
  SetupWelcomePanel,
} from "./components/welcome-overlay/setup-welcome-overlay";
import { SetupGuideWidget } from "./components/widget/setup-guide-widget";
import { useSetupGuideOptional } from "./provider/use-setup-guide";
import type { IntakeAnswers, Product } from "./registry/types";
import * as css from "./setup-guide-host.module.css";

/**
 * Mounts the floating setup-guide UI surfaces (intake modal, bottom-right
 * widget, contextual modal renderer) inside the SetupGuideProvider tree.
 *
 * Renders nothing when:
 *   - flag is off (provider returns null context)
 *   - no team loaded yet
 *   - no active product (legacy merchant or fresh-with-no-row)
 *   - widget state hides the panel for this device
 *
 * Each surface owns its own visibility logic; this component just
 * forwards props from the hook so the design-system components stay
 * test-friendly with simple props.
 */
export const SetupGuideHost = memo(function SetupGuideHost() {
  const setupGuide = useSetupGuideOptional();
  const team = useContext(TeamContext);
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const reloadTeam = useRequiredContext(ReloadTeamContext);

  // In-session welcome overlay flag. Flipped true the moment intake
  // submit succeeds; flipped false on Got It. Intentionally NOT
  // persisted — the overlay is a one-shot transition celebration, so
  // a reload mid-confetti just dismisses it (the checklist still
  // shows). Avoids the localStorage / DB-column complexity of trying
  // to model "have you ever seen this?".
  const [showWelcomeOverlay, setShowWelcomeOverlay] = useState(false);

  const handleIntakeSubmit = useCallback(
    async (product: Product, answers: IntakeAnswers) => {
      if (!setupGuide) return;
      await setupGuide.submitIntake(product, answers);
      // Disputes intake completion enables Reclaim immediately so the
      // merchant lands on the actual dashboard. Billing + provider
      // configuration stay async — handled later via setup-checklist
      // tasks. enableDisputes only writes settings.disputes.enabled,
      // independent of settings.disputes.billing.
      if (product === "disputes") {
        try {
          await merchantRpcClient.enableDisputes({});
        } catch (err) {
          // Non-fatal — the intake row is already persisted, so the
          // merchant can retry by re-clicking the gate's enable CTA
          // (which still works) or via the setup-checklist task.
          console.warn(
            "setup-guide: enableDisputes failed after intake submit",
            err,
          );
        }
        await reloadTeam();
        setShowWelcomeOverlay(true);
      }
    },
    [setupGuide, merchantRpcClient, reloadTeam],
  );

  if (!setupGuide || !team) return null;
  const {
    activeProduct,
    activeProgress,
    showIntake,
    widget,
    recordTaskVisited,
    signalTaskCompletion,
  } = setupGuide;

  if (!activeProduct) return null;

  // Welcome overlay only renders during the intake → dashboard
  // transition. Anchored visually to the widget so it must be
  // expanded too. Once dismissed via Got It in this session, never
  // returns until the merchant runs intake again.
  const showWelcomeOverlayNow =
    showWelcomeOverlay && !showIntake && widget.visualState === "expanded";

  const showWidget =
    !showIntake && activeProgress != null && widget.visualState === "expanded";

  return (
    <>
      {showIntake ? (
        <IntakeModal
          onSubmit={(answers) => handleIntakeSubmit(activeProduct, answers)}
          product={activeProduct}
        />
      ) : null}

      {showWelcomeOverlayNow ? <SetupWelcomeGradient /> : null}

      {showWidget || showWelcomeOverlayNow ? (
        <div className={css.bottomRightDock}>
          {showWelcomeOverlayNow ? (
            <SetupWelcomePanel onDismiss={() => setShowWelcomeOverlay(false)} />
          ) : null}
          {showWidget && activeProgress ? (
            <SetupGuideWidget
              onClose={widget.closeForDevice}
              onCollapse={widget.setCollapsed}
              onTaskClick={(taskId) => recordTaskVisited(activeProduct, taskId)}
              product={activeProduct}
              progress={activeProgress}
            />
          ) : null}
        </div>
      ) : null}

      <TaskContextualModalRenderer
        onTaskCompleted={(taskId) =>
          signalTaskCompletion({ product: activeProduct, taskId })
        }
        product={activeProduct}
      />
    </>
  );
});
